"use client";

import { Suspense, useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Link } from "@/i18n/routing";
import { signIn, getProviders, useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { Gift, Check } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CartDrawer from "@/components/cart/CartDrawer";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import { useToast } from "@/context/ToastContext";
import { trackEvent } from "@/lib/track";

type Tab = "signin" | "register";

export default function LoginPage() {
  return (
    <Suspense>
      <LoginContent />
    </Suspense>
  );
}

function LoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const t = useTranslations("auth");
  const tReferral = useTranslations("referral");
  const { data: session, status: sessionStatus } = useSession();
  const rawCallbackUrl = searchParams.get("callbackUrl") || "/";
  // Validate callbackUrl to prevent open redirect attacks
  const callbackUrl = (rawCallbackUrl.startsWith("/") && !rawCallbackUrl.includes("://")) ? rawCallbackUrl : "/";

  // Already-signed-in users shouldn't see the Sign In / Sign Up tabs at
  // all. Forward to the callbackUrl (or /account if none was specified)
  // so deep-links from CTAs like "Request a Wig Repair" route through
  // cleanly. Admins/drivers go to their own portals when no explicit
  // callback was given, matching completeSignIn's routing.
  useEffect(() => {
    if (sessionStatus !== "authenticated" || !session?.user) return;
    const role = (session.user as { role?: string }).role;
    const isAdminRole =
      role === "masteradmin" || role === "admin" || role === "manager";
    const isDriver = role === "driver";
    const isEmployee = role === "employee";
    const hasExplicitCallback = callbackUrl && callbackUrl !== "/";
    if (hasExplicitCallback) {
      router.replace(callbackUrl);
    } else if (isDriver) {
      router.replace("/driver");
    } else if (isAdminRole) {
      router.replace("/admin");
    } else if (isEmployee) {
      router.replace("/employee");
    } else {
      router.replace("/account");
    }
  }, [sessionStatus, session, callbackUrl, router]);
  // Check URL param first, then fall back to cookie set by /ref/[code] landing page
  const refParam = searchParams.get("ref") || "";
  const referralCode = refParam || (typeof document !== "undefined"
    ? (document.cookie.match(/(?:^|;\s*)ref_code=([^;]*)/)?.[1] ? decodeURIComponent(document.cookie.match(/(?:^|;\s*)ref_code=([^;]*)/)?.[1] || "") : "")
    : "");
  // Supports ?tab=register for links that should land on the signup form
  // (e.g. the URL submitted as the CTA for our 10DLC campaign).
  // ?inv=appt arrives from the signup-invite SMS — also lands on the
  // register tab (the SMS body promises an account-creation link).
  const initialTab: Tab =
    searchParams.get("tab") === "register" ||
    searchParams.get("inv") ||
    referralCode
      ? "register"
      : "signin";
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [loading, setLoading] = useState(false);

  // Referral state
  const [referrerName, setReferrerName] = useState("");
  const [referralValid, setReferralValid] = useState(false);

  // Sign In state
  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");

  // 2FA state — set when the credentials provider replies with TWO_FACTOR_REQUIRED.
  // We hold the email + password in component state and then re-submit them along
  // with the TOTP / backup code as a single signIn() call.
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);

  // Register state. Phone pre-fills from `?phone=` so signup-invite SMS
  // links land with the customer's own number already in the field —
  // makes the claim-preview banner appear immediately and the customer
  // doesn't have to retype.
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPhone, setRegPhone] = useState(() => searchParams.get("phone") || "");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirmPassword, setRegConfirmPassword] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);

  // Claim-preview: when registering, ping the server with the typed
  // phone/email so we can show "we found 3 appointments tied to this
  // phone — they'll appear in your account" before the user submits.
  // Debounced so we don't fire a request per keystroke.
  const [claimPreview, setClaimPreview] = useState<{
    appointments: number;
    orders: number;
    nextAppointmentAt: string | null;
  } | null>(null);
  useEffect(() => {
    if (activeTab !== "register") return;
    const phoneDigits = regPhone.replace(/\D/g, "");
    const hasPhone = phoneDigits.length >= 10;
    const hasEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail.trim());
    if (!hasPhone && !hasEmail) {
      setClaimPreview(null);
      return;
    }
    const handle = setTimeout(() => {
      const params = new URLSearchParams();
      if (hasPhone) params.set("phone", regPhone);
      if (hasEmail) params.set("email", regEmail);
      fetch(`/api/account/preview-claim?${params.toString()}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (!data) return;
          if ((data.appointments || 0) === 0 && (data.orders || 0) === 0) {
            setClaimPreview(null);
          } else {
            setClaimPreview(data);
          }
        })
        .catch(() => {
          // Silent — banner is purely informational, don't surface errors.
        });
    }, 600);
    return () => clearTimeout(handle);
  }, [regPhone, regEmail, activeTab]);

  // Validate referral code on mount
  useEffect(() => {
    if (referralCode) {
      fetch(`/api/referrals/validate?code=${encodeURIComponent(referralCode)}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.valid) {
            setReferralValid(true);
            setReferrerName(data.referrerName || "A friend");
          }
        })
        .catch(() => {});
    }
  }, [referralCode]);

  const completeSignIn = async () => {
    toast(t("signInSuccess"), "success");
    // Navigation is handled by the useEffect above that watches
    // sessionStatus. Having TWO redirect sources (here via router.push
    // to callbackUrl + useEffect via router.replace to role-appropriate
    // home) was racing — one would send the customer to "/" which the
    // Coming Soon middleware then bounced to /coming-soon, while the
    // other tried to land on /account. Result: users saw a stuck
    // "Redirecting..." state or ended up on the Coming Soon page
    // instead of their account dashboard. Reported 2026-04-15.
    //
    // Only refresh RSC trees so the middleware sees the new session
    // cookie on the next navigation. The useEffect handles the actual
    // route change.
    router.refresh();
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signInEmail || !signInPassword) {
      toast(t("emailAndPasswordRequired"), "error");
      return;
    }
    setLoading(true);
    trackEvent("signin_submitted");
    try {
      const result = await signIn("credentials", {
        email: signInEmail,
        password: signInPassword,
        redirect: false,
      });

      if (result?.error) {
        // Sentinel: account requires 2FA — switch the form to the code step.
        if (result.error === "TWO_FACTOR_REQUIRED") {
          setTwoFactorRequired(true);
          setTwoFactorCode("");
          setUseBackupCode(false);
        } else {
          toast(result.error, "error");
        }
      } else {
        await completeSignIn();
      }
    } catch {
      toast(t("signInError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = twoFactorCode.trim();
    if (!code) {
      toast("Enter the verification code", "error");
      return;
    }
    setLoading(true);
    try {
      const result = await signIn("credentials", {
        email: signInEmail,
        password: signInPassword,
        twoFactorToken: code,
        redirect: false,
      });
      if (result?.error) {
        toast(
          result.error === "TWO_FACTOR_REQUIRED"
            ? "Two-factor code required"
            : result.error,
          "error"
        );
      } else {
        await completeSignIn();
      }
    } catch {
      toast(t("signInError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const cancel2FA = () => {
    setTwoFactorRequired(false);
    setTwoFactorCode("");
    setUseBackupCode(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regEmail || !regPassword) {
      toast(t("emailAndPasswordRequired"), "error");
      return;
    }
    if (regPassword !== regConfirmPassword) {
      toast(t("passwordMismatch"), "error");
      return;
    }
    if (regPassword.length < 6) {
      toast(t("passwordTooShort"), "error");
      return;
    }
    setLoading(true);
    trackEvent("signup_submitted");
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: regName,
          email: regEmail,
          phone: regPhone,
          password: regPassword,
          smsOptIn,
          ...(referralCode ? { referralCode } : {}),
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || t("registrationFailed"));
      }

      toast(t("accountCreated"), "success");

      // Auto sign in after registration
      const result = await signIn("credentials", {
        email: regEmail,
        password: regPassword,
        redirect: false,
      });

      if (result?.error) {
        toast(t("accountCreatedSignIn"), "info");
        setActiveTab("signin");
        setSignInEmail(regEmail);
      } else {
        router.push(callbackUrl);
        router.refresh();
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : t("registrationFailed"), "error");
    } finally {
      setLoading(false);
    }
  };

  // Only hide the form when we're actively redirecting an authenticated
  // user. For unauthenticated + session-loading, render the form —
  // the previous `status === "loading"` guard here was trapping new
  // users on a "Loading..." screen while NextAuth's initial session
  // check completed, even though they have no session to redirect.
  // (2026-04-15 prod bug — reports of login/signup not working.)
  if (sessionStatus === "authenticated") {
    return (
      <>
        <Header />
        <CartDrawer />
        <main className="min-h-screen bg-brand-cream/30 flex items-center justify-center">
          <div className="text-sm text-brand-muted">Redirecting...</div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <CartDrawer />
      <main className="min-h-screen bg-brand-cream/30">
        <div className="container-luxury py-16 sm:py-24">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-10">
              <h1 className="font-display text-3xl text-brand-dark mb-2">
                {activeTab === "register" ? t("welcomeRegister") : t("welcome")}
              </h1>
              <p className="text-sm text-brand-muted">
                {activeTab === "register" ? t("welcomeRegisterSubtitle") : t("welcomeSubtitle")}
              </p>
            </div>

            <div className="bg-white border border-gray-100 shadow-sm">
              <div className="flex border-b border-gray-100">
                <button
                  onClick={() => { setActiveTab("signin"); trackEvent("auth_tab_switch", { tab: "signin" }); }}
                  className={cn(
                    "flex-1 py-4 text-xs font-medium uppercase tracking-widest transition-colors",
                    activeTab === "signin"
                      ? "text-brand-dark border-b-2 border-brand-gold"
                      : "text-brand-muted hover:text-brand-dark"
                  )}
                >
                  {t("signIn")}
                </button>
                <button
                  onClick={() => { setActiveTab("register"); trackEvent("auth_tab_switch", { tab: "signup" }); }}
                  className={cn(
                    "flex-1 py-4 text-xs font-medium uppercase tracking-widest transition-colors",
                    activeTab === "register"
                      ? "text-brand-dark border-b-2 border-brand-gold"
                      : "text-brand-muted hover:text-brand-dark"
                  )}
                >
                  {t("createAccount")}
                </button>
              </div>

              <div className="p-5 sm:p-8">
                {activeTab === "signin" && twoFactorRequired && (
                  <form onSubmit={handleVerify2FA} className="space-y-6 animate-fade-in">
                    <div className="text-center">
                      <h2 className="font-display text-xl text-brand-dark mb-1">
                        Two-factor authentication
                      </h2>
                      <p className="text-xs text-brand-muted">
                        {useBackupCode
                          ? "Enter one of your saved backup codes"
                          : "Enter the 6-digit code from your authenticator app"}
                      </p>
                    </div>

                    <Input
                      label={useBackupCode ? "Backup code" : "Verification code"}
                      type="text"
                      inputMode={useBackupCode ? "text" : "numeric"}
                      autoComplete="one-time-code"
                      placeholder={useBackupCode ? "ABC-XYZ-123" : "123456"}
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value)}
                      autoFocus
                    />

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-full"
                      disabled={loading}
                    >
                      {loading ? t("signingIn") : "Verify & Sign In"}
                    </Button>

                    <div className="flex items-center justify-between text-xs">
                      <button
                        type="button"
                        onClick={() => {
                          setUseBackupCode((v) => !v);
                          setTwoFactorCode("");
                        }}
                        className="text-brand-gold hover:text-brand-gold-dark transition-colors"
                      >
                        {useBackupCode
                          ? "Use authenticator app instead"
                          : "Use backup code instead"}
                      </button>
                      <button
                        type="button"
                        onClick={cancel2FA}
                        className="text-brand-muted hover:text-brand-dark transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {activeTab === "signin" && !twoFactorRequired && (
                  <div className="space-y-6 animate-fade-in">
                    {/* Social login buttons */}
                    <SocialLoginButtons callbackUrl={callbackUrl} />

                    <form onSubmit={handleSignIn} className="space-y-6">
                      <Input
                        label={t("email")}
                        type="email"
                        placeholder={t("emailPlaceholder")}
                        value={signInEmail}
                        onChange={(e) => setSignInEmail(e.target.value)}
                      />
                      <Input
                        label={t("password")}
                        type="password"
                        placeholder={t("passwordPlaceholder")}
                        value={signInPassword}
                        onChange={(e) => setSignInPassword(e.target.value)}
                      />

                      <div className="flex items-center justify-end">
                        <Link
                          href="/forgot-password"
                          onClick={() => trackEvent("forgot_password_click")}
                          className="text-xs text-brand-gold hover:text-brand-gold-dark transition-colors"
                        >
                          {t("forgotPassword")}
                        </Link>
                      </div>

                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        className="w-full"
                        disabled={loading}
                      >
                        {loading ? t("signingIn") : t("signInButton")}
                      </Button>

                      <p className="text-xs text-center text-brand-muted">
                        {t("noAccount")}{" "}
                        <button
                          type="button"
                          onClick={() => setActiveTab("register")}
                          className="text-brand-gold hover:text-brand-gold-dark transition-colors"
                        >
                          {t("createOne")}
                        </button>
                      </p>
                    </form>
                  </div>
                )}

                {activeTab === "register" && (
                  <div className="space-y-6 animate-fade-in">
                    {/* Social login — same buttons on both tabs so users
                        can create an account via Google/Apple without
                        filling the form. NextAuth auto-creates the account
                        on first social sign-in. */}
                    <SocialLoginButtons callbackUrl={callbackUrl} />

                  <form onSubmit={handleRegister} className="space-y-6">
                    {/* Account Benefits */}
                    <div className="bg-brand-cream/40 border border-brand-gold/10 p-4 space-y-2">
                      {(["accountBenefit1", "accountBenefit2", "accountBenefit3", "accountBenefit4"] as const).map((key) => (
                        <div key={key} className="flex items-center gap-2">
                          <Check className="h-3.5 w-3.5 text-brand-gold shrink-0" />
                          <span className="text-xs text-brand-dark">{t(key)}</span>
                        </div>
                      ))}
                    </div>

                    {referralValid && (
                      <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 text-green-800 text-sm">
                        <Gift className="h-5 w-5 shrink-0 text-green-600" />
                        <span>{tReferral("referredBy", { name: referrerName })}</span>
                      </div>
                    )}
                    <Input
                      label={t("fullName")}
                      placeholder={t("namePlaceholder")}
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                    />
                    <Input
                      label={t("email")}
                      type="email"
                      placeholder={t("emailPlaceholder")}
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                    />
                    <Input
                      label={t("phone")}
                      type="tel"
                      placeholder={t("phonePlaceholder")}
                      value={regPhone}
                      onChange={(e) => setRegPhone(e.target.value)}
                    />
                    <div className="space-y-1.5">
                      <Input
                        label={t("password")}
                        type="password"
                        placeholder={t("createPasswordPlaceholder")}
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                      />
                      <p className="text-[11px] text-brand-muted">{t("passwordHint")}</p>
                    </div>
                    <Input
                      label={t("confirmPassword")}
                      type="password"
                      placeholder={t("confirmPasswordPlaceholder")}
                      value={regConfirmPassword}
                      onChange={(e) => setRegConfirmPassword(e.target.value)}
                    />

                    {/* SMS Opt-In */}
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={smsOptIn}
                        onChange={(e) => setSmsOptIn(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-gold focus:ring-brand-gold/50 shrink-0"
                      />
                      <span className="text-xs text-brand-muted leading-relaxed">
                        {t("smsOptInLabel")}{" "}
                        <Link
                          href="/sms-terms"
                          className="text-brand-gold hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t("smsTermsLink")}
                        </Link>
                      </span>
                    </label>

                    {/* Existing-records callout — shown once preview-claim
                        finds Appointments / Orders booked under this phone
                        or email but never linked to an account. Helps the
                        customer understand that creating an account will
                        retroactively show their booking history. */}
                    {claimPreview && (
                      <div className="flex items-start gap-3 p-4 bg-brand-cream/60 border border-brand-gold/30 text-brand-dark text-sm rounded-md">
                        <Check className="h-5 w-5 shrink-0 text-brand-gold mt-0.5" />
                        <div className="space-y-0.5">
                          <p className="font-medium">
                            We found {claimPreview.appointments > 0 && (
                              <>
                                {claimPreview.appointments} appointment{claimPreview.appointments === 1 ? "" : "s"}
                              </>
                            )}
                            {claimPreview.appointments > 0 && claimPreview.orders > 0 && " + "}
                            {claimPreview.orders > 0 && (
                              <>
                                {claimPreview.orders} order{claimPreview.orders === 1 ? "" : "s"}
                              </>
                            )}{" "}
                            tied to this {regPhone ? "phone" : "email"}.
                          </p>
                          <p className="text-xs text-brand-muted">
                            They&apos;ll appear in your account once you sign up
                            {claimPreview.nextAppointmentAt
                              ? ` — including your next appointment on ${new Date(claimPreview.nextAppointmentAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}.`
                              : "."}
                          </p>
                        </div>
                      </div>
                    )}

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-full"
                      disabled={loading}
                    >
                      {loading ? t("creating") : t("createButton")}
                    </Button>

                    <p className="text-xs text-center text-brand-muted">
                      {t("hasAccount")}{" "}
                      <button
                        type="button"
                        onClick={() => setActiveTab("signin")}
                        className="text-brand-gold hover:text-brand-gold-dark transition-colors"
                      >
                        {t("signIn")}
                      </button>
                    </p>
                  </form>
                  </div>
                )}
              </div>
            </div>

            <p className="text-xs text-center text-brand-muted mt-6">
              {t("agreeToTerms")}
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

function SocialLoginButtons({ callbackUrl }: { callbackUrl: string }) {
  const t = useTranslations("auth");
  const [hasGoogle, setHasGoogle] = useState(false);
  const [hasApple, setHasApple] = useState(false);
  const [hasFacebook, setHasFacebook] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);

  useEffect(() => {
    getProviders().then((providers) => {
      if (providers) {
        setHasGoogle(!!providers.google);
        setHasApple(!!providers.apple);
        setHasFacebook(!!providers.facebook);
      }
    });
  }, []);

  if (!hasGoogle && !hasApple && !hasFacebook) return null;

  const handleSocialLogin = (provider: string) => {
    setSocialLoading(provider);
    trackEvent("social_login_click", { provider });
    // Append `?from=auth` so the middleware can post-route non-customer
    // roles (driver / employee / reseller / admin) to their portal after
    // the OAuth round-trip lands them on `/`.
    const target = callbackUrl === "/" ? "/?from=auth" : callbackUrl;
    signIn(provider, { callbackUrl: target });
  };

  return (
    <>
      <div className="space-y-3">
        {hasGoogle && (
          <button
            type="button"
            onClick={() => handleSocialLogin("google")}
            disabled={socialLoading !== null}
            className={cn(
              "w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-200 bg-white text-sm font-medium text-brand-dark transition-colors hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            )}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            {socialLoading === "google" ? t("signingIn") : t("continueWithGoogle")}
          </button>
        )}

        {hasApple && (
          <button
            type="button"
            onClick={() => handleSocialLogin("apple")}
            disabled={socialLoading !== null}
            className={cn(
              "w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-200 bg-black text-sm font-medium text-white transition-colors hover:bg-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
            )}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            {socialLoading === "apple" ? t("signingIn") : t("continueWithApple")}
          </button>
        )}

        {hasFacebook && (
          <button
            type="button"
            onClick={() => handleSocialLogin("facebook")}
            disabled={socialLoading !== null}
            className={cn(
              "w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-200 bg-[#1877F2] text-sm font-medium text-white transition-colors hover:bg-[#166FE5] disabled:opacity-60 disabled:cursor-not-allowed"
            )}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
            {socialLoading === "facebook" ? t("signingIn") : t("continueWithFacebook")}
          </button>
        )}
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-4 text-brand-muted tracking-widest">
            {t("orDivider")}
          </span>
        </div>
      </div>
    </>
  );
}
